/**
 * NativeWidgetRuntime — the web half of the ViewIR runtime contract (§7).
 *
 * Renders a widget from its declarative blueprint over a feed, with NO widget
 * HTML anywhere in the path: blueprint → render context → kit components.
 * It satisfies the same host-callback surface as the WebView renderer, so it
 * can drop into the existing `WidgetRuntimeSurface` branch unchanged.
 *
 * Props
 *   widgetId        the widget's slug (selects the mock feed + the blueprint)
 *   blueprint       a parsed `<widget>.view.json` (required)
 *   feed            explicit host payload `{ entries, settings, pending }`;
 *                   omit to let the runtime source it from `dataMode`
 *   pageId          which page to render — defaults to the `entry: true` one
 *   pageArgs        host-supplied args for that page (item_id, session_id, …)
 *   appearance      'light' | 'dark'
 *   dataMode        'mock' (studio sample feed) | 'user' (the signed-in user's
 *                   real widget instance, when the widget is adopted)
 *   host            the host bridge object; the studio passes a mock
 *   reloadKey       bump to refetch the user feed — the host wrote a row (the
 *                   studio capture button) and the page must show it. No-op in mock mode.
 *   pendingAdds     host-owned in-flight captures merged into the feed's
 *                   `pending.adds`, so a capture that is still running draws
 *                   the kit's real skeleton placeholder (app parity: the app
 *                   pushes the same list while a session runs).
 *   interaction     an OPTIONAL host-owned `{ slotState, dispatch, ui, setUi }`
 *                   (see viewir/slots.js) — the studio strip hands the SAME one
 *                   to every card so they are one app in one state. Omitted
 *                   (the app), the page owns its own inbox as before.
 *   inspect         studio inspect mode `{ dim: Set<nodeId> }` — marks every
 *                   node with its blueprint id and grays the given subtrees.
 *                   Omitted (the app, and the studio's own live preview), the
 *                   render is unchanged.
 *   topInset        px the host's floating chrome (the back button) occupies.
 *                   The page starts BELOW it rather than under it — the app
 *                   does the same by adding `_hostBackButtonHeight` to the
 *                   safe-area padding it hands the interpreter.
 *   targetBuild     render AS this app build (§4.5 graceful degradation): a
 *                   component introduced after it degrades to its declared
 *                   fallback, so an author can see what an older app shows.
 *                   null (the app, and the studio's live preview) = latest.
 *   previewVersion  reserved: once `view.json` is baked into published
 *                   bundles this selects WHICH version's blueprint renders.
 *                   Threaded into the context as `preview_version` so a trace
 *                   can say which version produced a frame.
 *
 * LIVE UPDATE (§7.5): a new `feed` re-evaluates selectors and re-renders in
 * place — the component tree is never remounted, so scroll position, filter
 * chips, the open sheet and any local selection survive the push.
 */
import { useEffect, useMemo, useState } from 'react';
import useWidgetUserFeed from './useWidgetUserFeed';
import { buildRenderContext } from './viewir/context';
import { ViewIRPage } from './viewir/interpreter';
import { buildTokenStyle } from './viewir/tokens';
import { mockFeed, mockPageArgs } from './mock';
import { PageHeaderHostContext, pageHeaderStyle } from './viewir/pageHeaderContext';
import KitKeyframes from './KitKeyframes';

const EMPTY_FEED = { entries: [], settings: {}, pending: { adds: [], updates: [] } };

const PAGE_FOCUS_KEYS = [
  'item_id', 'entry_id', 'id', 'focus_entry_id', 'log_card_id', 'source_session_id',
  'session_id', 'grid_id', 'photo_url',
];

/**
 * 真实宿主定位优先于 Studio 的 mock 默认条目；两套 focus token 不能同时留在页面参数里。
 */
function runtimePageArgs(widgetId, pageId, supplied) {
  const explicit = supplied && typeof supplied === 'object' ? supplied : {};
  const fallback = { ...mockPageArgs(widgetId, pageId) };
  if (PAGE_FOCUS_KEYS.some((key) => explicit[key] != null && explicit[key] !== '')) {
    for (const key of PAGE_FOCUS_KEYS) delete fallback[key];
  }
  return { ...fallback, ...explicit };
}

export default function NativeWidgetRuntime({
  widgetId,
  blueprint,
  feed: feedProp = null,
  pageId = null,
  pageArgs: pageArgsProp = null,
  appearance = 'light',
  dataMode = 'mock',
  host = null,
  reloadKey = 0,
  pendingAdds = null,
  interaction = null,
  inspect = null,
  topInset = 0,
  headerLeading = null,
  headerActions = [],
  previewVersion = null,
  targetBuild = null,
  onReport = null,
  onTrace = null,
  onDataState = null,
}) {
  // ── the feed ──────────────────────────────────────────────────────────
  const live = useWidgetUserFeed(!feedProp && dataMode === 'user', widgetId, null, reloadKey);
  const userFeed = live.feed;
  const dataState = feedProp || dataMode !== 'user' ? 'ready'
    : live.state === 'error' ? `error: ${live.error}`
      : live.state === 'ready' && !live.feed?.entries?.length ? 'empty' : live.state;

  useEffect(() => { onDataState?.(dataState); }, [dataState, onDataState]);

  const feed = useMemo(() => {
    const base = feedProp || (dataMode === 'user' ? (userFeed || EMPTY_FEED) : mockFeed(widgetId));
    if (!pendingAdds?.length) return base;
    const pending = base.pending || {};
    return { ...base, pending: { ...pending, adds: [...pendingAdds, ...(pending.adds || [])] } };
  }, [feedProp, dataMode, userFeed, widgetId, pendingAdds]);

  // ── page args (page-local state the DATA lane owns: the selected day) ──
  const [pageArgs, setPageArgs] = useState(() => runtimePageArgs(widgetId, pageId, pageArgsProp));
  useEffect(() => {
    setPageArgs(runtimePageArgs(widgetId, pageId, pageArgsProp));
  }, [widgetId, pageId, pageArgsProp]);

  /**
   * IS THE PAGE STILL WAITING FOR ITS FEED? — the fact the render context needs
   * so a data-bound module can draw a skeleton instead of a confident zero.
   *
   * Three conditions, and every one of them is load-bearing:
   *   `dataMode === 'user'`  MOCK MODE IS NEVER LOADING. The studio's sample
   *      feed is synchronous, so a mock card that could reach this state would
   *      shimmer forever — a whole studio of frozen skeletons.
   *   `!feedProp`  an explicit feed IS the answer, whoever passed it.
   *   `dataState === 'loading'`  the fetch above, before it resolves. The other
   *      states are all answers: 'empty' and 'not-adopted' mean the widget
   *      really does hold nothing, and the page must show its authored empty.
   */
  const loading = dataMode === 'user' && !feedProp && dataState === 'loading';

  const ctx = useMemo(() => {
    const c = buildRenderContext({ blueprint, feed, pageArgs, appearance, targetBuild, loading });
    c.preview_version = previewVersion;
    return c;
  }, [blueprint, feed, pageArgs, appearance, previewVersion, targetBuild, loading]);

  const tokenStyle = useMemo(
    () => buildTokenStyle({ appearance, accent: blueprint?.accent }),
    [appearance, blueprint?.accent],
  );

  if (!blueprint) {
    return (
      <div style={{ padding: 24, fontSize: 13, color: '#8E8E93' }}>
        No blueprint loaded for “{widgetId}”.
      </div>
    );
  }

  const modernHeader = pageHeaderStyle(blueprint, pageId);
  return (
    <PageHeaderHostContext.Provider value={{ leading: headerLeading, actions: headerActions }}>
    <div style={{ ...tokenStyle, height: '100%', overflow: 'hidden', position: 'relative' }}>
      <KitKeyframes />
      <div
        data-vi-scroll="1"
        style={{
          height: '100%', overflowY: modernHeader ? 'hidden' : 'auto', overflowX: 'hidden', scrollbarWidth: 'none',
          // a phone absorbs its own wheel: at the top or the bottom of this
          // scroller the gesture STOPS here instead of chaining to the page the
          // phone sits on (the studio strip scrolls under the pointer otherwise)
          overscrollBehavior: 'contain',
          paddingTop: modernHeader ? 0 : (topInset || 0), boxSizing: 'border-box',
        }}
      >
        <ViewIRPage
          blueprint={blueprint}
          ctx={ctx}
          pageId={pageId}
          host={host}
          onPageArgs={(patch) => setPageArgs((prev) => ({ ...prev, ...patch }))}
          onReport={onReport}
          onTrace={onTrace}
          inspect={inspect}
          interaction={interaction}
        />
      </div>
    </div>
    </PageHeaderHostContext.Provider>
  );
}
